import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../api';
import { updateLanguage, detectBrowserLanguage } from '../i18n';

interface User {
  id: string;
  email: string;
  full_name: string;
  role: 'user' | 'admin';
  locale?: string;
}

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
  isAdmin: boolean;
  updateUserLocale: (locale: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in (from session/token)
    checkAuth();
  }, []);

  useEffect(() => {
    // Update language when user changes or on initial load
    if (user?.locale) {
      updateLanguage(user.locale);
    } else if (!user) {
      // If not authenticated, use browser language
      const browserLang = detectBrowserLanguage();
      updateLanguage(browserLang);
    }
  }, [user]);

  const checkAuth = async () => {
    try {
      const data = await api.auth.me();
      setUser(data.user);
    } catch (error) {
      console.error('Auth check failed:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const data = await api.auth.login(email, password);
      setUser(data.user);
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await api.auth.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  };

  const updateUserLocale = (locale: string) => {
    if (user) {
      setUser({ ...user, locale });
      updateLanguage(locale);
    }
  };

  const value = {
    user,
    isLoading,
    login,
    logout,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    updateUserLocale,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
